<template>
  <div data-test="json-to-csv-button" class="json-to-csv">
    <JsonCSV
      ref="jsoncsv"
      :name="fileName"
      :data="getData"
      @export-started="exportStarted"
      @export-finished="exportFinished"
    >
      <mc-tooltip appearance="neutral-default" fit="medium" position="top-center" width="250">
        <mc-button
          slot="trigger"
          :disabled="isExporting || bookings.length == 0"
          appearance="neutral"
          data-test="download-button-json"
        >
          <mc-loading-indicator v-if="isExporting" :label="$t('download.compiling')" orientation="horizontal" />
          <mc-text-and-icon v-else icon="tray-arrow-down" :label="$t(`download.button.label`)" />
        </mc-button>
        {{ $t("download.button.tooltip") }}
      </mc-tooltip>
    </JsonCSV>
  </div>
</template>
<script>
import JsonCSV from "vue-json-csv";
import { dateFormat } from "@/lib/filters";
import { checkVessel, getExportImportPage } from "@/lib/utilities";
import dayjs from "dayjs";

export default {
  name: "JsonToCSV",
  components: { JsonCSV },
  props: {
    bookings: {
      type: Array,
      required: true
    },
    viewName: {
      type: String,
      required: true
    },
    filterValue: {
      type: String,
      required: true
    },
    pageNumber: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      isExporting: false,
      startTime: null,
      endTime: null
    };
  },
  computed: {
    getData() {
      if (!this.isExporting) return [];
      switch (this.viewName) {
        case "Shipments":
          return this.prepareShipmentsData();
        case "ETA_Changes":
          return this.prepareETAChangesData();
        case "Air":
        case "Lcl":
          return this.prepareAirLclData();
        default:
          return [];
      }
    },
    fileName() {
      const page = getExportImportPage() === "IMP" ? "Arrival" : "Depart";
      return `${this.viewName}_${page}_${this.filterValue.replaceAll("_", "").replaceAll("_to_", "")}_${dayjs().format(
        "DD_MMMM_YYYY_HH_mm"
      )}_Page${this.pageNumber}.csv`;
    }
  },
  methods: {
    exportStarted() {
      this.isExporting = true;
      this.startTime = new Date().getTime();

      setTimeout(() => {
        this.$refs?.jsoncsv?.generate();
        this.isExporting = false;
      }, 500);
    },
    exportFinished() {
      this.endTime = new Date().getTime();
      this.$sendGAEvent({
        event: "DQCS",
        category: "SO_Download",
        action: "Click",
        label: { data: "DownloadCSVTime", timeInMs: this.endTime - this.startTime }
      });
    },
    getContainerList(shipment) {
      const containers = shipment?.shipments?.[0]?.containers;
      if (!Array.isArray(containers) || containers.length === 0) return "-";
      const ids = containers.map((container) => container.id).filter((id) => id && id.trim() !== "");
      if (ids.length === 0) return "-";
      return ids.join(", ");
    },
    prepareShipmentsData() {
      return this.bookings.map((shipment) => {
        return {
          "BL Number": shipment?.ladingNumber,
          "Booking reference": shipment?.bookedByRef,
          "Export service mode": shipment?.exportServiceMode,
          "Import service mode": shipment?.importServiceMode,
          "Departure date": dateFormat(shipment?.portOfLoading?.departure?.dateTime, "DD MMM YYYY"),
          "Departure time": dayjs(shipment?.portOfLoading?.departure?.dateTime).format("HH:mm"),
          "First port of loading": shipment?.portOfLoading?.cityName || "-",
          "Origin country": shipment?.placeOfReceipt?.countryName || shipment?.portOfLoading?.countryName || "-",
          "Origin city": shipment?.placeOfReceipt?.cityName || shipment?.portOfLoading?.cityName || "-",
          "Destination country":
            shipment?.placeOfDelivery?.countryName || shipment?.portOfDischarge?.countryName || "-",
          "Destination city": shipment?.placeOfDelivery?.cityName || shipment?.portOfDischarge?.cityName || "-",
          "Last port of discharge": shipment?.portOfDischarge?.cityName || "-",
          "Arrival date": dateFormat(shipment?.portOfDischarge?.arrival?.dateTime, "DD MMM YYYY"),
          "Arrival time": dayjs(shipment?.portOfDischarge?.arrival?.dateTime).format("HH:mm"),
          Vessel: checkVessel(
            shipment?.portOfLoading?.departure?.carriage?.vehicleName,
            shipment?.portOfDischarge?.arrival?.carriage?.vehicleName
          ),
          Commodity: this.$t(shipment?.cargo?.commodity),
          "Container count": shipment?.cargo?.containerQuantity,
          Containers: this.getContainerList(shipment)
        };
      });
    },
    prepareETAChangesData() {
      return this.bookings.map((shipment) => {
        return {
          "BL Number": shipment?.ladingNumber,
          "Booking reference": shipment?.bookedByRef,
          "Departure date": dateFormat(shipment?.portOfLoading?.departure?.dateTime, "DD MMM YYYY"),
          "Departure time": dayjs(shipment?.portOfLoading?.departure?.dateTime).format("HH:mm"),
          "Origin country": shipment?.placeOfReceipt?.countryName || shipment?.portOfLoading?.countryName || "-",
          "Origin city": shipment?.placeOfReceipt?.cityName || shipment?.portOfLoading?.cityName || "-",
          "Destination country":
            shipment?.placeOfDelivery?.countryName || shipment?.portOfDischarge?.countryName || "-",
          "Destination city": shipment?.placeOfDelivery?.cityName || shipment?.portOfDischarge?.cityName || "-",
          "Original Arrival date": dateFormat(shipment?.portOfDischarge?.arrival?.originalArrival, "DD MMM YYYY"),
          "Original Arrival time": dayjs(shipment?.portOfDischarge?.arrival?.originalArrival).format("HH:mm"),
          "New arrival date": dateFormat(shipment?.portOfDischarge?.arrival?.dateTime, "DD MMM YYYY"),
          "New arrival time": dayjs(shipment?.portOfDischarge?.arrival?.dateTime).format("HH:mm"),
          Vessel: checkVessel(
            shipment?.portOfLoading?.departure?.carriage?.vehicleName,
            shipment?.portOfDischarge?.arrival?.carriage?.vehicleName
          ),
          Commodity: this.$t(shipment?.cargo?.commodity),
          "Container count": shipment?.cargo?.containerQuantity,
          Containers: this.getContainerList(shipment)
        };
      });
    },
    prepareAirLclData() {
      return this.bookings.map((shipment) => {
        const sharedData = [
          {
            "BL Number": shipment?.tpDocNum || "-",
            "Booking reference": shipment?.bookingReference || "-",
            "Departure date": dateFormat(shipment.portOfLoading?.departure?.dateTime, "DD MMM YYYY"),
            "Departure time": dayjs(shipment.portOfLoading?.departure?.dateTime).format("HH:mm"),
            "Origin country": shipment?.placeOfReceipt?.countryName || shipment?.portOfLoading?.countryName || "-",
            "Origin city": shipment?.placeOfReceipt?.cityName || shipment?.portOfLoading?.cityName || "-",
            "Destination country":
              shipment?.placeOfDelivery?.countryName || shipment?.portOfDischarge?.countryName || "-",
            "Destination city": shipment?.placeOfDelivery?.cityName || shipment?.portOfDischarge?.cityName || "-",
            "Arrival date": dateFormat(shipment.portOfDischarge?.arrival?.dateTime, "DD MMM YYYY"),
            "Arrival time": dayjs(shipment.portOfDischarge?.arrival?.dateTime).format("HH:mm"),
            "Package count":
              shipment?.cargo?.packageQuantity && shipment?.cargo?.packageQuantityUnit
                ? `${shipment.cargo.packageQuantity} ${shipment.cargo.packageQuantityUnit}`
                : "-"
          }
        ];

        const lclDownloadData = {
          Vessel: checkVessel(
            shipment.portOfLoading?.departure?.carriage?.vehicleName,
            shipment.portOfDischarge?.arrival?.carriage?.vehicleName
          )
        };
        return this.viewName === "Air" ? sharedData[0] : { ...sharedData[0], ...lclDownloadData };
      });
    }
  }
};
</script>
